import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Heart, HandHeart, TrendingUp, ArrowRight, Quote } from "lucide-react";
import donImage from "@/assets/don.webp";
import { Button } from "@/components/ui/button";
import { DonationModal } from "@/components/DonationModal";

interface Donation {
  id: string;
  nickname: string;
  amount: number;
  created_at: string;
}

/* ── Fade-in on scroll ── */
function FadeInSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export const DonationFeed = () => {
  const [donations, setDonations] = useState<Donation[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [donorCount, setDonorCount] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const fetchDonations = async () => {
      const { data } = await supabase
        .from("donations")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(20);

      if (data) setDonations(data as Donation[]);

      const { data: allDonations } = await supabase
        .from("donations")
        .select("amount");

      if (allDonations) {
        const total = allDonations.reduce((sum: number, d: { amount: number }) => sum + Number(d.amount), 0);
        setTotalAmount(total);
        setDonorCount(allDonations.length);
      }
    };

    fetchDonations();
    const interval = setInterval(fetchDonations, 30000);
    return () => { clearInterval(interval); };
  }, []);

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "à l'instant";
    if (mins < 60) return `il y a ${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `il y a ${hours}h`;
    const days = Math.floor(hours / 24);
    return `il y a ${days}j`;
  };

  return (
    <section id="donations" className="relative py-20 md:py-28 overflow-hidden">
      {/* Zellige texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30Z' fill='none' stroke='%23c2410c' stroke-width='0.5'/%3E%3C/svg%3E")`,
        backgroundSize: "60px 60px",
      }} />

      {/* Radial glows */}
      <div className="absolute top-1/4 left-0 w-80 h-80 rounded-full bg-primary/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-0 w-64 h-64 rounded-full bg-accent/5 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">

          {/* ═══════ HEADER ═══════ */}
          <FadeInSection className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-body mb-6">
              <HandHeart className="w-3.5 h-3.5" /> Cagnotte solidaire
            </span>
            <img src={donImage} alt="Don solidaire" className="w-24 h-24 object-contain mx-auto mb-5" loading="lazy" width={512} height={512} />
            <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-4 leading-tight">
              Ensemble pour la Tunisie
              <br />
              <span className="text-gradient">🇹🇳❤️</span>
            </h2>
            <p className="text-muted-foreground font-body text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-6">
              Chaque don compte. Soutiens les familles tunisiennes dans le besoin — même sans achat !
            </p>
            <Button onClick={() => setModalOpen(true)} size="lg" className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-body font-semibold shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl px-8 py-5">
              <Heart className="w-5 h-5" />
              Faire un don maintenant
            </Button>
          </FadeInSection>

          {/* ═══════ STATS ═══════ */}
          <div className="grid grid-cols-2 gap-4 md:gap-6 mb-10">
            <FadeInSection delay={0}>
              <div className="bg-card/60 backdrop-blur-sm border border-border/60 rounded-2xl p-6 md:p-8 text-center hover:shadow-lg hover:border-primary/20 transition-all duration-300 group">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                  <TrendingUp className="w-6 h-6 text-primary" />
                </div>
                <div className="text-3xl md:text-4xl font-display font-bold text-foreground mb-1">
                  {totalAmount.toFixed(2)}$
                </div>
                <p className="text-sm font-body text-muted-foreground">Total collecté</p>
              </div>
            </FadeInSection>
            <FadeInSection delay={100}>
              <div className="bg-card/60 backdrop-blur-sm border border-border/60 rounded-2xl p-6 md:p-8 text-center hover:shadow-lg hover:border-primary/20 transition-all duration-300 group">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                  <Heart className="w-6 h-6 text-primary fill-primary" />
                </div>
                <div className="text-3xl md:text-4xl font-display font-bold text-foreground mb-1">
                  {donorCount}
                </div>
                <p className="text-sm font-body text-muted-foreground">Donateurs</p>
              </div>
            </FadeInSection>
          </div>

          {/* ═══════ LIVE FEED ═══════ */}
          <FadeInSection delay={200}>
            <div className="bg-card/50 backdrop-blur-sm border border-border/60 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300">
              <div className="px-5 py-4 border-b border-border/40 flex items-center gap-2 bg-primary/[0.03]">
                <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                <span className="font-display font-semibold text-foreground text-sm">Derniers dons en direct</span>
              </div>

              {donations.length === 0 ? (
                <div className="p-10 text-center">
                  <Heart className="w-8 h-8 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground font-body text-sm">
                    Sois le premier à donner ! 💛
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border/30 max-h-[320px] overflow-y-auto">
                  {donations.map((donation) => (
                    <div
                      key={donation.id}
                      className="flex items-center gap-3 px-5 py-3.5 hover:bg-primary/[0.03] transition-colors"
                    >
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Heart className="w-4 h-4 text-primary fill-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-display font-semibold text-foreground text-sm">
                          {donation.nickname}
                        </span>
                        <span className="text-muted-foreground text-xs ml-2">
                          {timeAgo(donation.created_at)}
                        </span>
                      </div>
                      <span className="font-display font-bold text-primary text-sm flex-shrink-0">
                        +{Number(donation.amount).toFixed(2)}$
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </FadeInSection>

          {/* ═══════ QUOTE CTA ═══════ */}
          <FadeInSection className="mt-12" delay={300}>
            <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border border-primary/20 p-8 md:p-10 text-center">
              <div className="absolute inset-0 opacity-[0.04]" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30Z' fill='none' stroke='%23c2410c' stroke-width='0.5'/%3E%3C/svg%3E")`,
                backgroundSize: "60px 60px",
              }} />
              <div className="relative">
                <Quote className="w-8 h-8 text-primary/20 mx-auto mb-4 rotate-180" />
                <blockquote className="text-lg md:text-xl font-display font-bold text-foreground leading-snug mb-2">
                  Un petit geste ici, un grand changement là-bas.
                </blockquote>
                <p className="font-body text-muted-foreground text-sm mb-5">
                  100% des dons sont reversés aux familles tunisiennes dans le besoin.
                </p>
                <Button
                  onClick={() => setModalOpen(true)}
                  variant="outline"
                  className="gap-2 border-primary/30 text-primary hover:bg-primary/5 rounded-xl font-body"
                >
                  Contribuer maintenant
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </FadeInSection>

        </div>

        <DonationModal open={modalOpen} onClose={() => setModalOpen(false)} />
      </div>
    </section>
  );
};
